/**
 * Adapted from PlayCanvas Engine's block-allocator.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

let nextAllocatorId = 1

interface BlockSnapshot {
  readonly block: MemBlock
  readonly offset: number
  readonly size: number
  readonly free: boolean
}

/** 仅供 World 的两阶段状态发布使用，调用方不得修改其中的 handle。 */
export interface BlockAllocatorCheckpoint {
  readonly allocatorId: number
  readonly capacity: number
  readonly usedSize: number
  readonly blocks: readonly BlockSnapshot[]
}

/** Stable allocation handle；defrag 只更新 offset，不替换 handle identity。 */
export class MemBlock {
  _offset = 0
  _size = 0
  _free = true
  _prev: MemBlock | null = null
  _next: MemBlock | null = null
  _prevFree: MemBlock | null = null
  _nextFree: MemBlock | null = null
  _bucket = -1
  _allocatorId = 0

  get offset(): number {
    return this._offset
  }

  get size(): number {
    return this._size
  }
}

/** PlayCanvas 对齐的持久一维 block allocator。 */
export class BlockAllocator {
  private readonly id = nextAllocatorId++
  private headAll: MemBlock | null = null
  private tailAll: MemBlock | null = null
  private readonly freeBucketHeads: Array<MemBlock | null> = []
  private pool: MemBlock[] = []
  private capacityValue = 0
  private usedSizeValue = 0
  private freeSizeValue = 0
  private freeRegionCount = 0

  constructor(
    capacity = 0,
    private readonly growMultiplier = 1.1
  ) {
    this.validateSize(capacity, true)
    if (!Number.isFinite(growMultiplier) || growMultiplier <= 1) {
      throw new Error(`BlockAllocator growMultiplier 必须大于 1: ${growMultiplier}`)
    }
    if (capacity > 0) {
      this.capacityValue = capacity
      this.freeSizeValue = capacity
      const block = this.obtain(0, capacity, true)
      this.headAll = block
      this.tailAll = block
      this.addToBucket(block)
    }
  }

  get capacity(): number {
    return this.capacityValue
  }

  get usedSize(): number {
    return this.usedSizeValue
  }

  get freeSize(): number {
    return this.freeSizeValue
  }

  get fragmentation(): number {
    return this.freeSizeValue > 0 ? 1 - 1 / this.freeRegionCount : 0
  }

  /** 本地 World two-phase publish 扩展；不改变冻结分配算法。 */
  createCheckpoint(): BlockAllocatorCheckpoint {
    const blocks: BlockSnapshot[] = []
    let block = this.headAll
    while (block) {
      blocks.push(
        Object.freeze({
          block,
          offset: block._offset,
          size: block._size,
          free: block._free,
        })
      )
      block = block._next
    }
    return Object.freeze({
      allocatorId: this.id,
      capacity: this.capacityValue,
      usedSize: this.usedSizeValue,
      blocks: Object.freeze(blocks),
    })
  }

  /** 回滚时恢复原 handle identity，并重建上游 main-list/free-bucket 派生结构。 */
  restoreCheckpoint(checkpoint: BlockAllocatorCheckpoint): void {
    if (checkpoint.allocatorId !== this.id) {
      throw new Error('BlockAllocator checkpoint 不属于当前 allocator')
    }

    const checkpointBlocks = new Set(checkpoint.blocks.map(({ block }) => block))
    const recycled = new Set<MemBlock>(this.pool)
    let current = this.headAll
    while (current) {
      const next = current._next
      if (!checkpointBlocks.has(current)) recycled.add(current)
      current = next
    }
    for (const { block } of checkpoint.blocks) recycled.delete(block)

    this.freeBucketHeads.length = 0
    this.freeRegionCount = 0
    this.capacityValue = checkpoint.capacity
    this.usedSizeValue = checkpoint.usedSize
    this.freeSizeValue = checkpoint.capacity - checkpoint.usedSize
    this.headAll = null
    this.tailAll = null

    let previous: MemBlock | null = null
    for (const snapshot of checkpoint.blocks) {
      const block = snapshot.block
      block._allocatorId = this.id
      block._offset = snapshot.offset
      block._size = snapshot.size
      block._free = snapshot.free
      block._prev = previous
      block._next = null
      block._prevFree = null
      block._nextFree = null
      block._bucket = -1
      if (previous) previous._next = block
      else this.headAll = block
      previous = block
      if (block._free) this.addToBucket(block)
    }
    this.tailAll = previous

    this.pool = [...recycled]
    for (const block of this.pool) this.resetReleasedBlock(block)
  }

  allocate(size: number): MemBlock | null {
    this.validateSize(size)
    const gap = this.findFreeBlock(size)
    if (!gap) return null

    this.usedSizeValue += size
    this.freeSizeValue -= size

    if (gap._size === size) {
      gap._free = false
      this.removeFromBucket(gap)
      return gap
    }

    const allocation = this.obtain(gap._offset, size, false)
    gap._offset += size
    gap._size -= size
    this.rebucket(gap)
    this.insertAfterInMainList(allocation, gap._prev)
    return allocation
  }

  free(block: MemBlock): void {
    this.assertOwned(block)
    if (block._free) throw new Error('BlockAllocator.free 收到无效或已释放 block')

    block._free = true
    this.usedSizeValue -= block._size
    this.freeSizeValue += block._size

    const previous = block._prev
    const next = block._next
    const previousFree = previous?._free ?? false
    const nextFree = next?._free ?? false

    if (previousFree && nextFree) {
      previous!._size += block._size + next!._size
      this.removeFromMainList(block)
      this.removeFromMainList(next!)
      this.removeFromBucket(next!)
      this.release(block)
      this.release(next!)
      this.rebucket(previous!)
    } else if (previousFree) {
      previous!._size += block._size
      this.removeFromMainList(block)
      this.release(block)
      this.rebucket(previous!)
    } else if (nextFree) {
      block._size += next!._size
      this.removeFromMainList(next!)
      this.removeFromBucket(next!)
      this.release(next!)
      this.addToBucket(block)
    } else {
      this.addToBucket(block)
    }
  }

  grow(newCapacity: number): void {
    this.validateSize(newCapacity, true)
    if (newCapacity <= this.capacityValue) return

    const added = newCapacity - this.capacityValue
    this.capacityValue = newCapacity
    this.freeSizeValue += added

    if (this.tailAll?._free) {
      this.tailAll._size += added
      this.rebucket(this.tailAll)
    } else {
      const block = this.obtain(this.capacityValue - added, added, true)
      this.insertAfterInMainList(block, this.tailAll)
      this.addToBucket(block)
    }
  }

  defrag(maxMoves = 0, result: Set<MemBlock> = new Set()): Set<MemBlock> {
    if (!Number.isSafeInteger(maxMoves) || maxMoves < 0) {
      throw new Error(`非法 BlockAllocator maxMoves: ${maxMoves}`)
    }
    result.clear()
    if (this.freeRegionCount === 0) return result
    if (maxMoves === 0) this.defragFull(result)
    else this.defragIncremental(maxMoves, result)
    return result
  }

  /** 数字 size 会原地替换为稳定 MemBlock handle。 */
  updateAllocation(toFree: readonly MemBlock[], toAllocate: Array<number | MemBlock>): boolean {
    for (const block of toFree) this.free(block)

    for (let index = 0; index < toAllocate.length; index++) {
      const candidate = toAllocate[index]
      if (candidate instanceof MemBlock) continue
      const block = this.allocate(candidate)
      if (block) {
        toAllocate[index] = block
        continue
      }

      let totalRemaining = candidate
      for (let rest = index + 1; rest < toAllocate.length; rest++) {
        const value = toAllocate[rest]
        if (typeof value === 'number') totalRemaining += value
      }
      const neededCapacity = this.usedSizeValue + totalRemaining
      const headroomCapacity = Math.ceil(neededCapacity * this.growMultiplier)
      if (headroomCapacity > this.capacityValue) this.grow(headroomCapacity)
      this.defrag(0)

      for (let rest = index; rest < toAllocate.length; rest++) {
        const value = toAllocate[rest]
        if (value instanceof MemBlock) continue
        const allocation = this.allocate(value)
        if (!allocation) throw new Error('BlockAllocator defrag 后仍无法分配')
        toAllocate[rest] = allocation
      }
      return true
    }
    return false
  }

  private bucketFor(size: number): number {
    return 31 - Math.clz32(size)
  }

  private addToBucket(block: MemBlock): void {
    const bucket = this.bucketFor(block._size)
    block._bucket = bucket
    while (bucket >= this.freeBucketHeads.length) this.freeBucketHeads.push(null)
    block._prevFree = null
    block._nextFree = this.freeBucketHeads[bucket]
    if (this.freeBucketHeads[bucket]) this.freeBucketHeads[bucket]._prevFree = block
    this.freeBucketHeads[bucket] = block
    this.freeRegionCount++
  }

  private removeFromBucket(block: MemBlock): void {
    const bucket = block._bucket
    if (block._prevFree) block._prevFree._nextFree = block._nextFree
    else this.freeBucketHeads[bucket] = block._nextFree
    if (block._nextFree) block._nextFree._prevFree = block._prevFree
    block._prevFree = null
    block._nextFree = null
    block._bucket = -1
    this.freeRegionCount--
  }

  private rebucket(block: MemBlock): void {
    const bucket = this.bucketFor(block._size)
    if (bucket === block._bucket) return
    this.removeFromBucket(block)
    this.addToBucket(block)
  }

  private obtain(offset: number, size: number, free: boolean): MemBlock {
    const block = this.pool.pop() ?? new MemBlock()
    block._allocatorId = this.id
    block._offset = offset
    block._size = size
    block._free = free
    block._prev = null
    block._next = null
    block._prevFree = null
    block._nextFree = null
    block._bucket = -1
    return block
  }

  private release(block: MemBlock): void {
    this.resetReleasedBlock(block)
    this.pool.push(block)
  }

  private resetReleasedBlock(block: MemBlock): void {
    block._allocatorId = 0
    block._offset = 0
    block._size = 0
    block._free = true
    block._prev = null
    block._next = null
    block._prevFree = null
    block._nextFree = null
    block._bucket = -1
  }

  private insertAfterInMainList(block: MemBlock, after: MemBlock | null): void {
    if (after === null) {
      block._prev = null
      block._next = this.headAll
      if (this.headAll) this.headAll._prev = block
      this.headAll = block
      if (!this.tailAll) this.tailAll = block
      return
    }
    block._prev = after
    block._next = after._next
    if (after._next) after._next._prev = block
    after._next = block
    if (this.tailAll === after) this.tailAll = block
  }

  private removeFromMainList(block: MemBlock): void {
    if (block._prev) block._prev._next = block._next
    else this.headAll = block._next
    if (block._next) block._next._prev = block._prev
    else this.tailAll = block._prev
    block._prev = null
    block._next = null
  }

  private findFreeBlock(size: number): MemBlock | null {
    const startBucket = this.bucketFor(size)
    const length = this.freeBucketHeads.length
    if (startBucket < length) {
      let best: MemBlock | null = null
      let node = this.freeBucketHeads[startBucket]
      while (node) {
        if (node._size >= size && (!best || node._size < best._size)) {
          best = node
          if (node._size === size) break
        }
        node = node._nextFree
      }
      if (best) return best
    }
    for (let bucket = startBucket + 1; bucket < length; bucket++) {
      if (this.freeBucketHeads[bucket]) return this.freeBucketHeads[bucket]
    }
    return null
  }

  private defragFull(result: Set<MemBlock>): void {
    for (let bucket = 0; bucket < this.freeBucketHeads.length; bucket++) {
      let node = this.freeBucketHeads[bucket]
      while (node) {
        const nextFree = node._nextFree
        this.removeFromMainList(node)
        this.resetReleasedBlock(node)
        this.pool.push(node)
        node = nextFree
      }
      this.freeBucketHeads[bucket] = null
    }
    this.freeRegionCount = 0

    let offset = 0
    let block = this.headAll
    while (block) {
      if (block._offset !== offset) {
        block._offset = offset
        result.add(block)
      }
      offset += block._size
      block = block._next
    }
    const remaining = this.capacityValue - offset
    if (remaining > 0) {
      const freeBlock = this.obtain(offset, remaining, true)
      this.insertAfterInMainList(freeBlock, this.tailAll)
      this.addToBucket(freeBlock)
    }
  }

  private defragIncremental(maxMoves: number, result: Set<MemBlock>): void {
    const phase1Moves = Math.ceil(maxMoves / 2)
    const phase2Moves = maxMoves - phase1Moves
    for (let index = 0; index < phase1Moves; index++) {
      let lastAllocation = this.tailAll
      while (lastAllocation?._free) lastAllocation = lastAllocation._prev
      if (!lastAllocation) break
      const gap = this.findFreeBlock(lastAllocation._size)
      if (!gap || gap._offset >= lastAllocation._offset) break
      this.moveBlock(lastAllocation, gap)
      result.add(lastAllocation)
    }

    let block = this.headAll
    for (let moves = 0; moves < phase2Moves && block; ) {
      const next = block._next
      if (block._free && next && !next._free) {
        const allocation = next
        const freeBlock = block
        allocation._offset = freeBlock._offset
        freeBlock._offset = allocation._offset + allocation._size

        const previous = freeBlock._prev
        const following = allocation._next
        allocation._prev = previous
        allocation._next = freeBlock
        freeBlock._prev = allocation
        freeBlock._next = following
        if (previous) previous._next = allocation
        else this.headAll = allocation
        if (following) following._prev = freeBlock
        else this.tailAll = freeBlock

        if (freeBlock._next?._free) {
          const right = freeBlock._next
          freeBlock._size += right._size
          this.removeFromMainList(right)
          this.removeFromBucket(right)
          this.release(right)
          this.rebucket(freeBlock)
        }
        result.add(allocation)
        moves++
        block = freeBlock._next
      } else {
        block = next
      }
    }
  }

  private moveBlock(block: MemBlock, gap: MemBlock): void {
    if (block._free || !gap._free || gap._size < block._size) {
      throw new Error('BlockAllocator.moveBlock 收到非法 block/gap')
    }
    const blockSize = block._size
    const newOffset = gap._offset
    const previous = block._prev
    this.removeFromMainList(block)

    const freed = this.obtain(block._offset, blockSize, true)
    this.insertAfterInMainList(freed, previous)
    this.addToBucket(freed)
    if (freed._next?._free) {
      const right = freed._next
      freed._size += right._size
      this.removeFromMainList(right)
      this.removeFromBucket(right)
      this.release(right)
      this.rebucket(freed)
    }
    if (freed._prev?._free) {
      const left = freed._prev
      left._size += freed._size
      this.removeFromMainList(freed)
      this.removeFromBucket(freed)
      this.release(freed)
      this.rebucket(left)
    }

    block._offset = newOffset
    if (gap._size === blockSize) {
      const gapPrevious = gap._prev
      this.removeFromMainList(gap)
      this.removeFromBucket(gap)
      this.release(gap)
      this.insertAfterInMainList(block, gapPrevious)
    } else {
      gap._offset += blockSize
      gap._size -= blockSize
      this.rebucket(gap)
      this.insertAfterInMainList(block, gap._prev)
    }
  }

  private assertOwned(block: MemBlock): void {
    if (block._allocatorId !== this.id) throw new Error('MemBlock 不属于当前 BlockAllocator')
  }

  private validateSize(size: number, allowZero = false): void {
    if (!Number.isSafeInteger(size) || size < (allowZero ? 0 : 1)) {
      throw new Error(`非法 BlockAllocator size: ${size}`)
    }
  }
}
